import React, {
  useCallback, useEffect, useRef, useState,
} from 'react';
import PropTypes from 'prop-types';
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle,
} from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { useLocalizedMessage } from 'util/useLocalizedMessage';

/** Shared route and browser guard for any editable administration surface. */
export const useUnsavedChangesGuard = (dirty) => {
  const history = useHistory();
  const bypass = useRef(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);

  useEffect(() => {
    if (!dirty || !history || typeof history.block !== 'function') {
      setPendingNavigation(null);
      return undefined;
    }
    return history.block((location, action) => {
      if (bypass.current) return true;
      setPendingNavigation({ location, action });
      return false;
    });
  }, [dirty, history]);

  useEffect(() => {
    if (!dirty) return undefined;
    const warnBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', warnBeforeUnload);
    return () => window.removeEventListener('beforeunload', warnBeforeUnload);
  }, [dirty]);

  const navigateWithoutPrompt = useCallback((navigate) => {
    bypass.current = true;
    navigate();
    window.setTimeout(() => { bypass.current = false; }, 0);
  }, []);

  const keepEditing = useCallback(() => setPendingNavigation(null), []);
  const discardAndLeave = useCallback(() => {
    if (!pendingNavigation || !history) return;
    const { action, location } = pendingNavigation;
    setPendingNavigation(null);
    navigateWithoutPrompt(() => {
      if (action === 'REPLACE') history.replace(location);
      else history.push(location);
    });
  }, [history, navigateWithoutPrompt, pendingNavigation]);

  return {
    discardAndLeave,
    keepEditing,
    navigateWithoutPrompt,
    open: Boolean(pendingNavigation),
  };
};

export const UnsavedChangesDialog = ({ guard, message }) => {
  const t = useLocalizedMessage();
  return (
    <Dialog
      className="mp-dialog"
      open={guard.open}
      onClose={guard.keepEditing}
      maxWidth="xs"
      fullWidth
      aria-labelledby="unsaved-changes-dialog-title"
    >
      <DialogTitle id="unsaved-changes-dialog-title">
        {t('merchandising.unsaved.title')}
      </DialogTitle>
      <DialogContent>
        <p className="mp-dialog-copy">{message || t('merchandising.unsaved.body')}</p>
      </DialogContent>
      <DialogActions className="mp-dialog-actions">
        <Button variant="outlined" onClick={guard.discardAndLeave}>
          {t('merchandising.unsaved.discard')}
        </Button>
        <Button autoFocus variant="contained" color="primary" onClick={guard.keepEditing}>
          {t('merchandising.unsaved.keepEditing')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

UnsavedChangesDialog.propTypes = {
  message: PropTypes.string,
  guard: PropTypes.shape({
    discardAndLeave: PropTypes.func.isRequired,
    keepEditing: PropTypes.func.isRequired,
    open: PropTypes.bool.isRequired,
  }).isRequired,
};

UnsavedChangesDialog.defaultProps = { message: null };
